/**
 * RoyalShopModal.ts
 * 
 * Shared Royal Shop Modal component used both in Main Menu and In-World Gameplay.
 * Syncs product items dynamically with localStorage ('terra_anima_shop_items')
 * which is managed directly via Admin Page's Shop Tab.
 */

import { Currency } from '../../core/Currency';

export interface ShopProductItem {
  id: string;
  name: string;
  category: 'currency' | 'pass' | 'bag' | 'ticket' | 'dye' | 'potion' | 'custom';
  icon: string;
  desc: string;
  priceType: 'krw' | 'gems' | 'gold';
  price: number;
  available: boolean;
}

export interface ShopUserCurrency {
  gold: number;
  gems: number;
}

export const SHOP_ITEMS_STORAGE_KEY = 'terra_anima_shop_items';

export const DEFAULT_SHOP_PRODUCTS: ShopProductItem[] = [
  {
    id: 'gem_500',
    name: '보석 주머니 (500개)',
    category: 'currency',
    icon: '💎',
    desc: '모험 지원 편의 다이아몬드 보석 주머니',
    priceType: 'krw',
    price: 5500,
    available: true,
  },
  {
    id: 'gem_1200',
    name: '보석 상자 (1,200개)',
    category: 'currency',
    icon: '💎',
    desc: '보너스 200개 추가 증정 보석 상자',
    priceType: 'krw',
    price: 11000,
    available: true,
  },
  {
    id: 'ride_pass',
    name: '프리미엄 승마 패스 (30일)',
    category: 'pass',
    icon: '🎫',
    desc: '전 지역 마구간 4배속 전용 무료 승마권',
    priceType: 'gems',
    price: 150,
    available: true,
  },
  {
    id: 'inv_bag',
    name: '차원 인벤토리 확장 가방',
    category: 'bag',
    icon: '🎒',
    desc: '인벤토리 무게 한도 +50% 증가 및 편의 지원',
    priceType: 'gems',
    price: 100,
    available: true,
  },
  {
    id: 'char_rename',
    name: '외형 & 이름 변경권',
    category: 'ticket',
    icon: '✨',
    desc: '언제든 외모와 이름을 자유롭게 변경할 수 있는 허가서',
    priceType: 'gems',
    price: 80,
    available: true,
  },
  {
    id: 'dye_royal_purple',
    name: '로열 퍼플 고급 염료',
    category: 'dye',
    icon: '🎨',
    desc: '의복 및 망토를 찬란한 왕실 보라색으로 염색',
    priceType: 'gems',
    price: 50,
    available: true,
  },
  {
    id: 'potion_exp_buff',
    name: '태양의 경험치 영약 (3개)',
    category: 'potion',
    icon: '🧪',
    desc: '1시간 동안 몬스터 사냥 경험치 획득량 2배 증가',
    priceType: 'gold',
    price: Currency.toCopper({ gold: 100 }), // 100골드
    available: true,
  },
];

export class RoyalShopModal {
  private el: HTMLElement | null = null;
  private isOpen = false;
  private getUserCurrency: () => ShopUserCurrency;

  constructor(getUserCurrency: () => ShopUserCurrency) {
    this.getUserCurrency = getUserCurrency;
    this.buildDOM();
  }

  public static loadProducts(): ShopProductItem[] {
    try {
      const raw = localStorage.getItem(SHOP_ITEMS_STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      console.warn('Failed to parse shop items', e);
    }
    // 기본 상점 아이템 등록
    localStorage.setItem(SHOP_ITEMS_STORAGE_KEY, JSON.stringify(DEFAULT_SHOP_PRODUCTS));
    return DEFAULT_SHOP_PRODUCTS;
  }

  private buildDOM(): void {
    const existing = document.getElementById('shared-royal-shop-modal');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.id = 'shared-royal-shop-modal';
    wrapper.className = 'modal-backdrop hidden';
    wrapper.style.cssText = `
      position: fixed; inset: 0; z-index: 99999;
      display: none; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.75); backdrop-filter: blur(4px);
    `;

    wrapper.innerHTML = `
      <div class="modal-card" style="
        background: #0f182c; border: 1.5px solid #f59e0b; border-radius: 12px;
        width: 720px; max-width: 95vw; max-height: 85vh; box-shadow: 0 16px 40px rgba(0,0,0,0.85);
        color: #f8fafc; font-family: 'Malgun Gothic', -apple-system, sans-serif;
        overflow: hidden; display: flex; flex-direction: column;
      ">
        <div style="display:flex; justify-content:space-between; align-items:center; padding:14px 18px; border-bottom:1px solid #1e293b; background:#0b1120;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:22px;">💎</span>
            <span style="font-size:16px; font-weight:800; color:#fef08a;">솔라리스 왕립 상점 (Royal Shop)</span>
          </div>
          <button id="btn-close-shared-shop" style="background:transparent; border:none; color:#94a3b8; font-size:18px; cursor:pointer;">✕</button>
        </div>

        <div style="padding: 16px 20px; overflow-y: auto; flex: 1;">
          <!-- User Currency Bar -->
          <div style="display:flex; justify-content:space-between; align-items:center; background:#1e293b; padding:10px 14px; border-radius:8px; margin-bottom:14px; border:1px solid #334155;">
            <span style="font-size:13px; color:#cbd5e1;">보유 재화:</span>
            <div style="display:flex; gap:16px; font-weight:bold; font-size:13.5px;">
              <span id="shared-shop-gold" style="color:#fde047;">🪙 100G</span>
              <span id="shared-shop-gems" style="color:#38bdf8;">💎 100개</span>
            </div>
          </div>

          <!-- Product Grid Dynamically Rendered -->
          <div id="shared-shop-products-grid" style="display:grid; grid-template-columns: 1fr 1fr; gap:12px;">
            <!-- Rendered by renderProducts() -->
          </div>
        </div>

        <div style="display:flex; justify-content:flex-end; padding:12px 20px; border-top:1px solid #1e293b; background:#0b1120;">
          <button id="btn-confirm-shared-shop" style="background:#2563eb; border:1px solid #60a5fa; color:#fff; padding:8px 18px; border-radius:6px; font-weight:bold; cursor:pointer; font-size:13px;">닫기</button>
        </div>
      </div>
    `;

    document.body.appendChild(wrapper);
    this.el = wrapper;

    wrapper.querySelector('#btn-close-shared-shop')?.addEventListener('click', () => this.close());
    wrapper.querySelector('#btn-confirm-shared-shop')?.addEventListener('click', () => this.close());
  }

  public renderProducts(): void {
    if (!this.el) return;
    const grid = this.el.querySelector('#shared-shop-products-grid');
    if (!grid) return;

    const products = RoyalShopModal.loadProducts().filter(p => p.available !== false);
    grid.innerHTML = '';

    products.forEach((p) => {
      const priceBadge = p.priceType === 'krw'
        ? `<span style="color:#38bdf8; font-weight:bold;">₩ ${p.price.toLocaleString()}</span>`
        : p.priceType === 'gems'
        ? `<span style="color:#fde047; font-weight:bold;">💎 ${p.price.toLocaleString()} 보석</span>`
        : `<span style="color:#4ade80; font-weight:bold;">🪙 ${Currency.format(p.price)}</span>`;

      const card = document.createElement('div');
      card.style.cssText = 'background:#090f1d; border:1px solid #334155; border-radius:8px; padding:14px; text-align:center; display:flex; flex-direction:column; justify-content:space-between;';
      card.innerHTML = `
        <div>
          <div style="font-size:28px; margin-bottom:4px;">${p.icon || '📦'}</div>
          <div style="font-size:14px; font-weight:bold; color:#f8fafc;">${p.name}</div>
          <div style="font-size:11.5px; color:#94a3b8; margin:4px 0 8px;">${p.desc}</div>
        </div>
        <div>
          <div style="font-size:13px; margin-bottom:8px;">${priceBadge}</div>
          <button class="shop-buy-btn" data-item="${p.id}" style="width:100%; padding:7px; background:#2563eb; border:1px solid #60a5fa; color:#fff; border-radius:6px; font-weight:bold; cursor:pointer; font-size:12px;">
            ${p.priceType === 'krw' ? '충전하기' : '구매하기'}
          </button>
        </div>
      `;

      card.querySelector('.shop-buy-btn')?.addEventListener('click', () => {
        alert(`[${p.name}] 테스트 결제 및 아이템 지급 시스템 준비 중입니다.`);
      });

      grid.appendChild(card);
    });
  }

  public open(): void {
    if (!this.el) return;
    this.refreshCurrency();
    this.renderProducts();
    this.el.classList.remove('hidden');
    this.el.style.display = 'flex';
    this.isOpen = true;
  }

  public close(): void {
    if (!this.el) return;
    this.el.classList.add('hidden');
    this.el.style.display = 'none';
    this.isOpen = false;
  }

  public toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  public refreshCurrency(): void {
    if (!this.el) return;
    const cur = this.getUserCurrency();
    const goldEl = this.el.querySelector('#shared-shop-gold');
    const gemsEl = this.el.querySelector('#shared-shop-gems');
    if (goldEl) goldEl.textContent = `🪙 ${Currency.format(cur.gold || 0)}`;
    if (gemsEl) gemsEl.textContent = `💎 ${(cur.gems || 0).toLocaleString()}개`;
  }
}
